import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { almacen } from '../api';
import { Cargando, ConAyuda, Fallo, Filtro, Pastilla, Tarjeta, Vacio } from '../componentes';
import { cantidad, dia } from '../formato';
import { mayuscula, useVocabulario } from '../vocabulario';

/**
 * La lista del mercado: lo que se va a vender mañana, según lo que se vendió
 * los mismos días de las semanas anteriores.
 *
 * El mínimo sirve para el arroz; para el pescado del ceviche no. El pescado se
 * compra cada madrugada y lo que manda es cuánto ceviche sale un sábado, no
 * cuánto cabe en la cámara. Por eso es otra lista y no otro número en la de
 * reposición: aquí entra lo que se vendió aunque esté sobre su mínimo.
 *
 * Es una lista para llevar, no una orden: al mercado no se le firma nada. Lo
 * que se quiera pedir a un proveedor con orden sigue en «Por mínimo».
 */
export function ListaDelMercado() {
  const vocabulario = useVocabulario();
  const [semanas, setSemanas] = useState(4);

  const consulta = useQuery({
    queryKey: ['lista-del-mercado', semanas],
    queryFn: () => almacen.marketList(undefined, semanas),
  });

  const lista = consulta.data;

  return (
    <>
      <div className="filtros">
        {[2, 4, 8].map((n) => (
          <Filtro
            key={n}
            texto={`Últimas ${n} semanas`}
            activo={semanas === n}
            onClick={() => setSemanas(n)}
          />
        ))}
      </div>

      {consulta.isError ? (
        <Fallo error={consulta.error} onReintentar={() => consulta.refetch()} />
      ) : null}

      {consulta.isLoading || lista === undefined ? (
        consulta.isError ? null : <Cargando que="la lista del mercado" />
      ) : (
        <>
          <p className="nota">
            {/*
             * De dónde sale cada número: decirlo es lo que hace que se crea, y
             * deja ver un feriado metido en la cuenta.
             */}
            Para el {dia(lista.day)}: lo que salió de promedio los{' '}
            {lista.lookedAt.map((d) => dia(d)).join(', ')}, menos lo que hay y lo
            ya pedido. Lo vencido no cuenta como que hay.
          </p>

          {lista.groups.length === 0 ? (
            <Vacio
              titulo="No hace falta comprar nada"
              mensaje={`Con lo que hay alcanza para lo que se vendió esos días. Si el ${vocabulario.insumo} no lleva receta o no se vendió, no aparece aquí.`}
            />
          ) : (
            lista.groups.map((grupo) => (
              <Tarjeta key={grupo.supplierId ?? 'sin-proveedor'}>
                <div className="tarjeta__cabeza">
                  <h2 className="tarjeta__titulo">{grupo.supplier}</h2>
                  {grupo.supplierId === null ? <Pastilla texto="Sin proveedor" tono="aviso" /> : null}
                </div>
                <div className="tabla-envoltura">
                  <table>
                    <thead>
                      <tr>
                        <th>{mayuscula(vocabulario.insumo)}</th>
                        <th className="numero">
                          <ConAyuda ayuda="Lo que salió de promedio cada uno de esos días, por ventas y producción.">
                            Sale
                          </ConAyuda>
                        </th>
                        <th className="numero">
                          <ConAyuda ayuda="Lo que queda hoy, sin lo vencido.">Hay</ConAyuda>
                        </th>
                        <th className="numero">
                          <ConAyuda ayuda="Lo que ya está en una orden de compra sin recibir.">Pedido</ConAyuda>
                        </th>
                        <th className="numero">Comprar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {grupo.lines.map((line) => (
                        <tr key={line.supplyId}>
                          <td>
                            <strong>{line.name}</strong>
                          </td>
                          <td className="numero mono apagado">{cantidad(line.average)}</td>
                          <td className="numero mono apagado">{cantidad(line.onHand)}</td>
                          <td className="numero mono apagado">{cantidad(line.onOrder)}</td>
                          <td className="numero mono">
                            <strong>{cantidad(line.suggested)}</strong>{' '}
                            <span className="apagado">{line.unit}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tarjeta>
            ))
          )}
        </>
      )}
    </>
  );
}
